import { useState, useEffect } from 'react';
import { getAllEquipment, getAvailableEquipment } from '../../services/supabase';
import { Equipment } from '../../types';

interface EquipmentSelectorProps {
    onSelect: (equipment: Equipment, quantity: number) => void;
    dateRange: [Date | null, Date | null];
    compact?: boolean;
    excludeIds?: string[];
}

const EquipmentSelector: React.FC<EquipmentSelectorProps> = ({ onSelect, dateRange, compact = false, excludeIds = [] }) => {
    const [equipment, setEquipment] = useState<Equipment[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedEquipment, setSelectedEquipment] = useState<Equipment | null>(null);
    const [quantity, setQuantity] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');

    // Загрузка доступного оборудования
    useEffect(() => {
        const fetchEquipment = async () => {
            setIsLoading(true);
            setError(null);

            try {
                console.log('EquipmentSelector: Начинаем загрузку оборудования');
                console.log('EquipmentSelector: dateRange:', dateRange);
                let equipmentData: any[] = [];

                // Если даты выбраны, то получаем доступное оборудование на этот период
                if (dateRange[0] && dateRange[1]) {
                    const startDate = dateRange[0].toISOString();
                    const endDate = dateRange[1].toISOString();
                    console.log(`EquipmentSelector: Запрашиваем доступное оборудование на период: ${startDate} - ${endDate}`);

                    const response = await getAvailableEquipment(startDate, endDate);
                    if (response.error) {
                        console.error('EquipmentSelector: Ошибка при получении доступного оборудования:', response.error);
                        
                        // Если произошла ошибка при запросе доступного оборудования, запрашиваем все оборудование
                        console.log('EquipmentSelector: Запрашиваем все оборудование после ошибки');
                        const allEquipmentResponse = await getAllEquipment();
                        if (allEquipmentResponse.error) {
                            throw new Error(allEquipmentResponse.error.message);
                        }
                        equipmentData = allEquipmentResponse.data || [];
                    } else {
                        equipmentData = response.data || [];

                        // Если для выбранных дат нет доступного оборудования, загружаем все оборудование
                        if (equipmentData.length === 0) {
                            console.log('EquipmentSelector: Нет доступного оборудования на выбранные даты, загружаем всё оборудование');
                            const allEquipmentResponse = await getAllEquipment();
                            if (allEquipmentResponse.error) {
                                throw new Error(allEquipmentResponse.error.message);
                            }
                            equipmentData = allEquipmentResponse.data || [];
                            console.log(`EquipmentSelector: Загружено ${equipmentData.length} единиц всего оборудования`);
                        }
                    }
                    console.log(`EquipmentSelector: Получено ${equipmentData.length} единиц оборудования`);
                } else {
                    // Иначе получаем все оборудование
                    console.log('EquipmentSelector: Запрашиваем все оборудование (нет диапазона дат)');
                    const response = await getAllEquipment();
                    if (response.error) {
                        throw new Error(response.error.message);
                    }
                    equipmentData = response.data || [];
                    console.log(`EquipmentSelector: Получено ${equipmentData.length} единиц оборудования`);
                }

                // Преобразуем данные к типу Equipment[] и фильтруем доступное оборудование
                const formattedEquipment: Equipment[] = equipmentData.map(item => {
                    console.log('EquipmentSelector: Форматирование элемента оборудования:', item.name);
                    return {
                        id: item.id,
                        name: item.name,
                        description: item.description,
                        category_id: item.category_id,
                        daily_rate: item.daily_rate,
                        quantity: item.quantity || 0,
                        image_url: item.image_url || null,
                        year: item.year || null,
                        manufacturer: item.manufacturer || null,
                        model: item.model || null,
                        status: item.status || 'available',
                        created_at: item.created_at || null,
                        updated_at: item.updated_at || null,
                        sort_order: item.sort_order || null,
                        available: typeof item.is_available !== 'undefined' ? item.is_available : true
                    };
                });

                const availableEquipment = formattedEquipment.filter(item => item.available);
                console.log(`EquipmentSelector: Доступно ${availableEquipment.length} единиц оборудования после фильтрации`);
                
                // Если после фильтрации оборудования нет, просто отображаем все, что есть
                if (availableEquipment.length === 0 && formattedEquipment.length > 0) {
                    console.log('EquipmentSelector: Нет доступного оборудования, показываем все оборудование без фильтрации');
                    setEquipment(formattedEquipment);
                } else {
                    setEquipment(availableEquipment);
                }
            } catch (err) {
                console.error('EquipmentSelector: Ошибка при загрузке оборудования:', err);
                setError('Ошибка при загрузке оборудования. Пожалуйста, попробуйте позже.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchEquipment();
    }, [dateRange]);

    // Фильтруем оборудование по поисковому запросу
    useEffect(() => {
        console.log(`EquipmentSelector: Фильтрация по поисковому запросу "${searchTerm}"`);
        console.log(`EquipmentSelector: Всего оборудования для фильтрации: ${equipment.length}`);
        
        if (excludeIds.length > 0) {
            console.log(`EquipmentSelector: Исключаем ${excludeIds.length} единиц уже выбранного оборудования:`, excludeIds);
        }
    }, [searchTerm, equipment, excludeIds]);

    // Фильтруем оборудование по поисковому запросу
    const filteredEquipment = equipment.filter(item => {
        // Исключаем оборудование, которое уже добавлено
        if (excludeIds.includes(item.id)) return false;
        
        if (!searchTerm) return true;
        
        const search = searchTerm.toLowerCase().trim();
        
        // Если поисковый запрос пустой, возвращаем все оборудование
        if (!search) return true;
        
        // Проверяем все возможные поля, в которых может быть найден поисковый запрос
        const nameMatch = item.name ? item.name.toLowerCase().includes(search) : false;
        const modelMatch = item.model ? item.model.toLowerCase().includes(search) : false;
        const manufacturerMatch = item.manufacturer ? item.manufacturer.toLowerCase().includes(search) : false;
        const descriptionMatch = item.description ? item.description.toLowerCase().includes(search) : false;
        
        return nameMatch || modelMatch || manufacturerMatch || descriptionMatch;
    });

    // Определим классы в зависимости от compact режима
    const resultContainerClasses = compact
        ? "mt-2 border border-gray-200 rounded-lg p-2 bg-gray-50 max-h-[15vh] overflow-y-auto"
        : "mt-2 border border-gray-200 rounded-lg p-4 bg-gray-50";

    const equipmentItemClasses = compact
        ? "flex justify-between items-center p-2 hover:bg-gray-100 rounded border-b border-gray-100 last:border-b-0"
        : "flex justify-between items-center p-3 hover:bg-gray-100 rounded-lg mb-2";

    const inputClasses = compact
        ? "w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white text-sm"
        : "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white";

    return (
        <div>
            {/* Поле поиска */}
            <input
                type="text"
                placeholder="Поиск оборудования..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={inputClasses}
            />

            {/* Контейнер с результатами */}
            <div className={resultContainerClasses}>
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mb-2"></div>
                        <p className="text-gray-600 text-sm">Загрузка оборудования...</p>
                    </div>
                ) : error ? (
                    <div className="p-3 bg-red-100 text-red-700 rounded-md">
                        <p className="text-sm">{error}</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="text-xs underline mt-1 text-red-600"
                        >
                            Обновить страницу
                        </button>
                    </div>
                ) : filteredEquipment.length > 0 ? (
                    <ul className={compact ? "space-y-1" : "space-y-2"}>
                        {filteredEquipment.map((item) => (
                            <li key={item.id} className={equipmentItemClasses}>
                                <div className="flex items-center space-x-2">
                                    {item.image_url && !compact && (
                                        <img
                                            src={item.image_url}
                                            alt={item.name}
                                            className="w-12 h-12 object-cover rounded"
                                        />
                                    )}
                                    {item.image_url && compact && (
                                        <img
                                            src={item.image_url}
                                            alt={item.name}
                                            className="w-8 h-8 object-cover rounded"
                                        />
                                    )}
                                    <div>
                                        <p className={compact ? "font-medium text-sm" : "font-medium"}>{item.name}</p>
                                        <p className={compact ? "text-xs text-gray-500" : "text-sm text-gray-500"}>
                                            {(item.daily_rate !== undefined && item.daily_rate !== null && item.daily_rate > 0) 
                                                ? `${item.daily_rate.toLocaleString('ru-RU')} ₽/день`
                                                : 'Цена не указана'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center">
                                    <div className="flex items-center mr-2">
                                        <button
                                            type="button"
                                            onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                                            className={compact ? "px-1 py-0.5 text-xs bg-gray-100 rounded-l" : "px-2 py-1 bg-gray-100 rounded-l"}
                                        >
                                            -
                                        </button>
                                        <span className={compact ? "px-2 py-0.5 text-xs bg-gray-50" : "px-3 py-1 bg-gray-50"}>
                                            {selectedEquipment?.id === item.id ? quantity : 1}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setQuantity((prev) => prev + 1)}
                                            className={compact ? "px-1 py-0.5 text-xs bg-gray-100 rounded-r" : "px-2 py-1 bg-gray-100 rounded-r"}
                                        >
                                            +
                                        </button>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSelectedEquipment(item);
                                            onSelect(item, selectedEquipment?.id === item.id ? quantity : 1);
                                            setQuantity(1);
                                        }}
                                        className={compact ? "px-2 py-0.5 text-xs bg-primary text-white rounded-md" : "px-3 py-1 bg-primary text-white rounded-md"}
                                    >
                                        Добавить
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : searchTerm ? (
                    <div className="text-center py-4">
                        <p className="text-gray-500">Оборудование по запросу "{searchTerm}" не найдено</p>
                        <button
                            onClick={() => setSearchTerm('')}
                            className="text-primary text-sm underline mt-1"
                        >
                            Очистить поиск
                        </button>
                    </div>
                ) : equipment.length > 0 && excludeIds.length === equipment.length ? (
                    <div className="text-center py-4">
                        <p className="text-gray-500">Всё доступное оборудование уже добавлено</p>
                    </div>
                ) : (
                    <div className="text-center py-4">
                        <p className="text-gray-500">Оборудование не найдено</p>
                        <p className="text-xs text-gray-400 mt-1">В системе нет доступного оборудования. Пожалуйста, добавьте оборудование через панель администратора.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EquipmentSelector; 